import { OrderLineItemFragment } from '#src/generated/graphql'
import { toast } from 'react-toastify'
import { guestCartStore } from '#src/stores'
import keys from 'lodash/keys'

export function getFilteredLineItems(lineItems: OrderLineItemFragment[]) {
  return lineItems.filter(
    lineItem =>
      !['OrderLineItemShipmentSource', 'OrderLineItemExpertTipSource'].includes(
        lineItem.source.__typename
      )
  )
}

export interface GroupedLineItemsType {
  parentLineItem: OrderLineItemFragment
  children: OrderLineItemFragment[]
}

export function groupLineItems({
  lineItems,
  includeTip = false,
  includeShipping = false
}: {
  lineItems: OrderLineItemFragment[]
  includeTip?: boolean
  includeShipping?: boolean
}) {
  const orderLineItemTree: {
    [key: string]: GroupedLineItemsType
  } = {}

  lineItems.forEach(orderLineItem => {
    if (!includeTip && orderLineItem.source.__typename === 'OrderLineItemExpertTipSource') return
    if (!includeShipping && orderLineItem.source.__typename === 'OrderLineItemShipmentSource')
      return

    if (orderLineItem.isGroup) {
      // If it's a group, it gets it's own ID, overwrite?
      orderLineItemTree[orderLineItem.id] = {
        parentLineItem: orderLineItem,
        children: []
      }
    } else {
      if (orderLineItem.parentId && orderLineItemTree[orderLineItem.parentId]) {
        orderLineItemTree[orderLineItem.parentId].children.push(orderLineItem)
      } else {
        orderLineItemTree[orderLineItem.id] = { parentLineItem: orderLineItem, children: [] }
      }
    }
  })

  return keys(orderLineItemTree).map(lineItemTree => orderLineItemTree[lineItemTree])
}

export type GuestSellable = {
  id: string
  quantity: number
}

export type GuestOrder = GuestSellable[]

export function getGuestCartContents(): GuestOrder {
  return guestCartStore.get('cart') || []
}

export function addGuestCartItem(sellableId: string) {
  const guestSellables = getGuestCartContents()

  // Check if this already exists so we don't add duplicates
  const existingSellableIndex = guestSellables.findIndex(
    guestSellable => guestSellable.id === sellableId
  )

  // If it exists, just increment the quantity instead of adding a new entry
  if (existingSellableIndex !== -1) {
    guestSellables[existingSellableIndex].quantity =
      guestSellables[existingSellableIndex].quantity + 1
  } else {
    guestSellables.push({
      id: sellableId,
      quantity: 1
    })
  }

  guestCartStore.set('cart', guestSellables)
}

export function removeGuestCartItem(sellableId: string) {
  const guestSellables = getGuestCartContents()
  const updatedGuestSellables = guestSellables.filter(
    guestSellable => guestSellable.id !== sellableId
  )

  if (guestSellables.length === updatedGuestSellables.length) {
    toast.error('Error removing that product')
  }

  guestCartStore.set('cart', updatedGuestSellables)
}

export function updateGuestCartItemQuantity(sellableId: string, quantity: number) {
  const guestSellables = getGuestCartContents()

  const sellableIndexToUpdate = guestSellables.findIndex(
    guestSellable => guestSellable.id === sellableId
  )

  if (!guestSellables[sellableIndexToUpdate]) {
    return
  }

  guestSellables[sellableIndexToUpdate].quantity = quantity
  guestCartStore.set('cart', guestSellables)
}

export function resetGuestCart() {
  guestCartStore.remove('cart')
}
